/**
 * Supervisor dashboard chart cards.
 * Reuses the clinician analytics TRANSFORMS (processChartData) so the numbers
 * match exactly what clinicians see, and renders supervisor-focused views.
 * Works identically for the practice-wide AGGREGATE (all clinicians' records
 * concatenated) and per-clinician DRILL-DOWN (one clinician's records).
 *
 * Dashboards: Caseload Diagnosis, No-Show, Treatment Plan Review, PHQ-9, GAD-7.
 * Revenue (income-by-month) and Risk Level Distribution are intentionally absent.
 */
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie
} from 'recharts';
import { processChartData as processCaseload } from '../Analytics/CaseloadDiagnosis/CaseloadDiagnosisChart';
import { processChartData as processNoShow } from '../Analytics/NoShowAnalysis/NoShowChart';
import { processChartData as processTPR } from '../Analytics/TreatmentPlanReview/TreatmentPlanReviewChart';

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
    <div className="mb-4">
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const Stat = ({ label, value, tone = 'default' }) => {
  const tones = {
    default: 'text-gray-900', good: 'text-green-600', warn: 'text-amber-600', bad: 'text-red-600', brand: 'text-blue-600'
  };
  return (
    <div className="text-center px-3 py-2 bg-gray-50 rounded-lg">
      <div className={`text-2xl font-bold ${tones[tone]}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
};

const Empty = () => (
  <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No data recorded yet</div>
);

// ---------- Caseload Diagnosis ----------
export const CaseloadCard = ({ data }) => {
  if (!data || data.length === 0) return <Card title="Caseload Diagnosis"><Empty /></Card>;
  const r = processCaseload(data);
  const bars = r.diagnosisData.slice(0, 8);
  return (
    <Card title="Caseload Diagnosis" subtitle={`${r.uniqueClients} clients · ${r.totalDiagnoses} distinct diagnoses`}>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Stat label="Clients" value={r.uniqueClients} tone="brand" />
        <Stat label="Diagnoses" value={r.totalDiagnoses} />
        <Stat label="Diversity" value={r.diversityIndex} />
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={bars} layout="vertical" margin={{ left: 20, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} />
          <YAxis type="category" dataKey="diagnosis" width={150} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            {bars.map((b, i) => <Cell key={i} fill={b.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs text-gray-500 mt-2 capitalize">Practice profile: {r.practiceType}</p>
    </Card>
  );
};

// ---------- No-Show ----------
export const NoShowCard = ({ data }) => {
  if (!data || data.length === 0) return <Card title="No-Show Analysis"><Empty /></Card>;
  const r = processNoShow(data);
  const dist = [
    { band: 'Low (<25%)', count: r.lowRiskCount, color: '#22c55e' },
    { band: 'Moderate (25-35%)', count: r.moderateRiskCount, color: '#f59e0b' },
    { band: 'High (35-50%)', count: r.highRiskCount, color: '#f472b6' },
    { band: 'Critical (>50%)', count: r.criticalCount, color: '#ef4444' }
  ];
  const rateTone = r.practiceNoShowRate < 25 ? 'good' : r.practiceNoShowRate < 35 ? 'warn' : 'bad';
  return (
    <Card title="No-Show Analysis" subtitle={`${r.totalClients} clients · ${r.totalAppointments} appointments`}>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Stat label="No-Show Rate" value={`${r.practiceNoShowRate}%`} tone={rateTone} />
        <Stat label="Attendance" value={`${r.attendanceRate}%`} tone="good" />
        <Stat label="Critical Clients" value={r.criticalCount} tone={r.criticalCount ? 'bad' : 'good'} />
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={dist} margin={{ left: 0, right: 10 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="band" tick={{ fontSize: 10 }} />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {dist.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

// ---------- Treatment Plan Review ----------
const TPR_COLORS = { 'Overdue': '#e74c3c', 'Due Soon': '#f39c12', 'On Time': '#27ae60' };
export const TPRCard = ({ data }) => {
  if (!data || data.length === 0) return <Card title="Treatment Plan Review"><Empty /></Card>;
  const processed = processTPR(data);
  const counts = { 'Overdue': 0, 'Due Soon': 0, 'On Time': 0 };
  processed.forEach(p => { counts[p.status] = (counts[p.status] || 0) + 1; });
  const pie = Object.entries(counts).map(([status, value]) => ({ status, value, color: TPR_COLORS[status] }));
  const total = processed.length;
  return (
    <Card title="Treatment Plan Review" subtitle={`${total} active treatment plans`}>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Stat label="Overdue" value={counts['Overdue']} tone={counts['Overdue'] ? 'bad' : 'good'} />
        <Stat label="Due Soon" value={counts['Due Soon']} tone="warn" />
        <Stat label="On Time" value={counts['On Time']} tone="good" />
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={pie} dataKey="value" nameKey="status" cx="50%" cy="50%" outerRadius={70} label={(e) => `${e.status}: ${e.value}`}>
            {pie.map((p, i) => <Cell key={i} fill={p.color} />)}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  );
};

// ---------- PHQ-9 / GAD-7 severity distribution (latest score per client) ----------
const PHQ9_BANDS = [
  { label: 'Minimal', min: 0, max: 4, color: '#a3d977' },
  { label: 'Mild', min: 5, max: 9, color: '#f6d743' },
  { label: 'Moderate', min: 10, max: 14, color: '#feb24c' },
  { label: 'Mod. Severe', min: 15, max: 19, color: '#fb6a4a' },
  { label: 'Severe', min: 20, max: 27, color: '#cb181d' }
];
const GAD7_BANDS = [
  { label: 'Minimal', min: 0, max: 4, color: '#a3d977' },
  { label: 'Mild', min: 5, max: 9, color: '#f6d743' },
  { label: 'Moderate', min: 10, max: 14, color: '#feb24c' },
  { label: 'Severe', min: 15, max: 21, color: '#cb181d' }
];

function severityDistribution(data, scoreField, bands) {
  // latest score per client
  const latest = {};
  data.forEach(row => {
    const id = row.clientId;
    if (!latest[id] || row.date > latest[id].date) latest[id] = row;
  });
  const scores = Object.values(latest).map(r => r[scoreField]).filter(s => typeof s === 'number');
  const dist = bands.map(b => ({ ...b, count: scores.filter(s => s >= b.min && s <= b.max).length }));
  const mean = scores.length ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10 : 0;
  const elevated = scores.filter(s => s >= 10).length; // moderate+
  return { dist, clientCount: scores.length, mean, elevated };
}

const ScoreCard = ({ title, subtitle, data, scoreField, bands }) => {
  if (!data || data.length === 0) return <Card title={title}><Empty /></Card>;
  const { dist, clientCount, mean, elevated } = severityDistribution(data, scoreField, bands);
  if (clientCount === 0) return <Card title={title}><Empty /></Card>;
  return (
    <Card title={title} subtitle={subtitle}>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Stat label="Clients Tracked" value={clientCount} tone="brand" />
        <Stat label="Mean (latest)" value={mean} />
        <Stat label="Moderate+ " value={elevated} tone={elevated ? 'warn' : 'good'} />
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={dist} margin={{ left: 0, right: 10 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {dist.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs text-gray-500 mt-2">Distribution of each client's most recent score by severity band.</p>
    </Card>
  );
};

export const PHQ9Card = ({ data }) => (
  <ScoreCard title="PHQ-9 Depression Severity" subtitle="Latest score per client" data={data} scoreField="phq9Score" bands={PHQ9_BANDS} />
);
export const GAD7Card = ({ data }) => (
  <ScoreCard title="GAD-7 Anxiety Severity" subtitle="Latest score per client" data={data} scoreField="gad7Score" bands={GAD7_BANDS} />
);

/** Render all five supervisor dashboards for a given data scope. */
export const SupervisorDashboards = ({ scopeData }) => (
  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
    <CaseloadCard data={scopeData['caseload-diagnosis']} />
    <NoShowCard data={scopeData['no-show-analysis']} />
    <TPRCard data={scopeData['treatment-plan-review']} />
    <PHQ9Card data={scopeData['individual-phq9-tracker']} />
    <GAD7Card data={scopeData['individual-gad7-tracker']} />
  </div>
);
